import React from 'react';
import { Formik } from 'formik';

import { STATUSES } from 'constants/common';

import { getTranslation } from 'helpers/getTranslation';
import { getLessonStatusContextItems } from 'helpers/getLessonStatusContextItems';
import { getReadonlyLessonText } from 'helpers/getReadonlyLessonText';
import { isLessonSaveVisible } from 'helpers/isLessonSaveVisible';

import { AutoSave } from 'components/AutoSave';
import { LessonLayout } from 'components/Layout';
import { LessonHeader } from 'components/LessonHeader';
import { LessonLocaleTabs } from 'components/LessonLocaleTabs';
import { Loading } from 'components/shared/Loading';
import { ToolbarActions } from 'components/ToolbarActions';
import { ReadOnlySection } from 'components/ReadOnlySection';
import { BreadcrumbsToolbar } from 'components/Toolbar';

import { Settings } from './components/Settings';
import { AnswersTypeSelect } from './components/AnswersTypeSelect';

import {
  convertDataToForm,
  convertDataToApi,
} from './ConnectionQuizPage.helpers';

import styles from './ConnectionQuizPage.module.scss';

const ConnectionQuizPage = ({
  saveLoading,
  autoSaveLoading,
  readOnly,
  baseLesson,
  lesson,
  localeContentLoading,
  lastActionAt,
  courseTitle,
  courseStatus,
  isCatalogLesson,
  activeLocale,
  availableLocales,
  localesWithContent,
  lessonType,
  onLocaleChange,
  onAutoSaveSubmit,
  onSaveClick,
  onPreviewClick,
  onRemoveClick,
  onStatusContextItemClick,
}: {
  saveLoading: any;
  autoSaveLoading: any;
  readOnly: any;
  baseLesson: any;
  lesson: any;
  localeContentLoading: boolean;
  lastActionAt: any;
  courseTitle: any;
  courseStatus: any;
  isCatalogLesson: any;
  activeLocale: string;
  availableLocales: readonly string[];
  localesWithContent: string[];
  lessonType: string;
  onLocaleChange: (locale: string) => void;
  onAutoSaveSubmit: any;
  onSaveClick: any;
  onPreviewClick: any;
  onRemoveClick: any;
  onStatusContextItemClick: any;
}) => {
  const shellLesson = baseLesson;

  if (localeContentLoading || !lesson) {
    return (
      <LessonLayout
        sidebarComponent={<div />}
        toolbarComponent={
          <BreadcrumbsToolbar
            breadcrumbsData={{
              courseTitle,
              lessonTitle: getTranslation('common.text.untitled'),
            }}
            endAdornment={
              <ToolbarActions
                withRemove
                withSave={isLessonSaveVisible({
                  status: shellLesson.status,
                  activeLocale,
                })}
                saveLoading={saveLoading}
                autoSaveLoading={autoSaveLoading}
                contextItems={getLessonStatusContextItems({
                  courseReadOnly:
                    isCatalogLesson || courseStatus === STATUSES.PUBLISHED,
                  status: shellLesson.status,
                })}
                removeData={{
                  id: shellLesson.id,
                  title: shellLesson.title,
                }}
                updatedAt={lastActionAt}
                status={shellLesson.status}
                onSaveClick={() => {
                  return;
                }}
                onPreviewClick={onPreviewClick}
                onRemoveClick={onRemoveClick}
                onStatusContextItemClick={() => {
                  return;
                }}
              />
            }
          />
        }
      >
        <div className={styles.page}>
          {readOnly && (
            <ReadOnlySection text={getReadonlyLessonText(shellLesson.status)} />
          )}
          <LessonLocaleTabs
            lessonId={shellLesson.id}
            lessonType={lessonType}
            activeLocale={activeLocale}
            availableLocales={availableLocales}
            localesWithContent={localesWithContent}
            onLocaleChange={onLocaleChange}
          />
          <div className={styles.inner}>
            <Loading />
          </div>
        </div>
      </LessonLayout>
    );
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={convertDataToForm(lesson, readOnly)}
      onSubmit={onAutoSaveSubmit}
    >
      {({ values }: { values: any }) => (
        <LessonLayout
          sidebarComponent={<div />}
          toolbarComponent={
            <BreadcrumbsToolbar
              breadcrumbsData={{ courseTitle, lessonTitle: values.title }}
              endAdornment={
                <ToolbarActions
                  withRemove
                  withSave={isLessonSaveVisible({
                    status: shellLesson.status,
                    activeLocale,
                  })}
                  saveLoading={saveLoading}
                  autoSaveLoading={autoSaveLoading}
                  contextItems={getLessonStatusContextItems({
                    courseReadOnly:
                      isCatalogLesson || courseStatus === STATUSES.PUBLISHED,
                    status: lesson.status,
                  })}
                  removeData={{ id: lesson.id, title: lesson.title }}
                  updatedAt={lastActionAt}
                  status={lesson.status}
                  onSaveClick={() => onSaveClick(values)}
                  onPreviewClick={onPreviewClick}
                  onRemoveClick={onRemoveClick}
                  onStatusContextItemClick={(contextItem: any) =>
                    onStatusContextItemClick(
                      contextItem,
                      convertDataToApi(values),
                    )
                  }
                />
              }
            />
          }
        >
          <div className={styles.page}>
            <AutoSave debounceMs={1000} />
            {readOnly && (
              <ReadOnlySection text={getReadonlyLessonText(lesson.status)} />
            )}
            <LessonHeader readOnly={readOnly} lessonRole={lesson.role} />
            <LessonLocaleTabs
              lessonId={lesson.id}
              lessonType={lessonType}
              activeLocale={activeLocale}
              availableLocales={availableLocales}
              localesWithContent={localesWithContent}
              onLocaleChange={onLocaleChange}
            />
            <div className={styles.inner}>
              {!lesson.answersType ? (
                <AnswersTypeSelect />
              ) : (
                <Settings readOnly={readOnly} />
              )}
            </div>
          </div>
        </LessonLayout>
      )}
    </Formik>
  );
};

export default React.memo(ConnectionQuizPage) as any;
